import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

import { Amount } from '@/components/common/Amount';
import { Button } from '@/components/common/Button';
import { Menu } from '@/components/common/Menu';
import { SearchInput } from '@/components/common/Input';
import {
  SvgCheveronDown,
  SvgDotsVertical,
  SvgExpand,
  SvgFilter,
  SvgAdd,
} from '@/components/icons';
import { theme } from '@/style/theme';
import { useAccounts, useAppStore } from '@/store/app';
import { useTransactionsStore } from '@/store/transactions';
import { currentMonth, addMonthLength } from '@/utils/months';
import { downloadCsv, transactionsToCsv } from '@/utils/csv';
import { TransactionsTable } from './TransactionsTable';

const PRESETS = [
  { label: '全部日期', resolve: () => ({ startDate: null, endDate: null }) },
  {
    label: '本月',
    resolve: () => ({
      startDate: `${currentMonth()}-01`,
      endDate: `${currentMonth()}-31`,
    }),
  },
  {
    label: '近 3 个月',
    resolve: () => ({
      startDate: `${addMonthLength(currentMonth(), -2)}-01`,
      endDate: `${currentMonth()}-31`,
    }),
  },
  {
    label: '近 60 天',
    resolve: () => {
      const start = new Date();
      start.setDate(start.getDate() - 60);
      return { startDate: start.toISOString().slice(0, 10), endDate: null };
    },
  },
];

function resolveAccountIds(
  accountId: string | undefined,
  accounts: ReturnType<typeof useAccounts>,
): { ids: string[] | null; title: string; showAccountColumn: boolean } {
  switch (accountId) {
    case 'all':
    case undefined:
      return {
        ids: accounts.filter(a => a.closed === 0).map(a => a.id),
        title: '全部账户',
        showAccountColumn: true,
      };
    case 'onbudget':
      return {
        ids: accounts
          .filter(a => a.offbudget === 0 && a.closed === 0)
          .map(a => a.id),
        title: '预算内',
        showAccountColumn: false,
      };
    case 'offbudget':
      return {
        ids: accounts
          .filter(a => a.offbudget === 1 && a.closed === 0)
          .map(a => a.id),
        title: '预算外',
        showAccountColumn: false,
      };
    default: {
      const account = accounts.find(item => item.id === accountId);
      return {
        ids: accountId ? [accountId] : null,
        title: account?.name ?? '账户',
        showAccountColumn: false,
      };
    }
  }
}

/**
 * Port of `components/accounts/Account.tsx` — the same component backs
 * /accounts, /accounts/onbudget, /accounts/offbudget and a single account.
 */
export function AccountPage({
  accountId: accountIdProp,
}: {
  accountId?: string;
}) {
  const params = useParams();
  const accountId = accountIdProp ?? params.accountId;
  const accounts = useAccounts();
  const load = useTransactionsStore(state => state.load);
  const transactions = useTransactionsStore(state => state.transactions);
  const loading = useTransactionsStore(state => state.loading);
  const setFilter = useTransactionsStore(state => state.setFilter);
  const create = useTransactionsStore(state => state.create);
  const [search, setSearch] = useState('');
  const [expandedSplits, setExpandedSplits] = useState(true);
  const refreshAccounts = useAppStore(state => state.refreshAccounts);

  const view = useMemo(() => resolveAccountIds(accountId, accounts), [accountId, accounts]);

  useEffect(() => {
    void load(view.ids);
    void refreshAccounts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountId]);

  const visible = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (!needle) return transactions;
    return transactions.filter(txn => {
      const payee = txn.payee_name?.toLowerCase() ?? '';
      const notes = txn.notes?.toLowerCase() ?? '';
      return payee.includes(needle) || notes.includes(needle);
    });
  }, [transactions, search]);

  const totalBalance = useMemo(
    () =>
      accounts
        .filter(account => view.ids?.includes(account.id))
        .reduce((sum, account) => sum + account.balance, 0),
    [accounts, view.ids],
  );

  function exportCsv() {
    const groups = useAppStore.getState().categoryGroups;
    const categoryNames = new Map<string, string>();
    for (const group of groups) {
      for (const category of group.categories) {
        categoryNames.set(category.id, category.name);
      }
    }
    const accountNames = new Map(
      accounts.map(account => [account.id, account.name]),
    );
    downloadCsv(
      view.title.replace(/\s+/g, '-').toLowerCase(),
      transactionsToCsv(visible, { accountNames, categoryNames }),
    );
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col" data-testid="account-page">
      <div className="shrink-0 px-5 pt-1">
        <h1 className="text-[25px] font-medium text-page-text" data-testid="account-title">
          {view.title}
        </h1>
        <div
          className="tnum pb-2 text-[25px] font-medium"
          style={{
            color:
              totalBalance < 0 ? theme.numberNegative : theme.numberPositive,
          }}
          data-testid="account-balance"
        >
          <Amount value={totalBalance} />
        </div>

        <div className="flex flex-wrap items-center gap-2 pb-2">
          <Button
            size="sm"
            variant="bare"
            icon={<SvgAdd width={12} height={12} />}
            data-testid="add-new-transaction"
            onClick={() => {
              const account = view.ids?.[0] ?? accounts[0]?.id;
              if (!account) return;
              void create({ account, date: new Date().toISOString().slice(0, 10), amount: 0 });
            }}
          >
            新增
          </Button>

          <Menu
            width={190}
            data-testid="date-filter"
            button={
              <span className="inline-flex">
                <Button
                  size="sm"
                  variant="bare"
                  icon={<SvgFilter width={12} height={12} />}
                >
                  筛选
                  <SvgCheveronDown width={11} height={11} />
                </Button>
              </span>
            }
            items={PRESETS.map(preset => ({
              label: preset.label,
              onSelect: () => {
                const range = preset.resolve();
                setFilter({
                  startDate: range.startDate,
                  endDate: range.endDate,
                });
              },
            }))}
          />

          <div className="flex-1" />

          <SearchInput
            className="w-[220px]"
            placeholder="搜索"
            aria-label="搜索交易"
            value={search}
            onChange={event => setSearch(event.target.value)}
          />

          <button
            type="button"
            title={expandedSplits ? '收起拆分交易' : '展开拆分交易'}
            data-testid="toggle-splits"
            onClick={() => setExpandedSplits(value => !value)}
            className="rounded-sm p-1.5 text-page-text-light hover:bg-button-bare-background-hover"
          >
            <SvgExpand width={15} height={15} />
          </button>

          <Menu
            width={210}
            data-testid="account-menu"
            button={
              <span className="inline-flex cursor-pointer rounded-sm p-1.5 text-page-text-light hover:bg-button-bare-background-hover">
                <SvgDotsVertical width={15} height={15} />
              </span>
            }
            items={[{ label: '导出', onSelect: exportCsv }]}
          />
        </div>
      </div>

      <TransactionsTable
        transactions={visible}
        loading={loading}
        showAccountColumn={view.showAccountColumn}
        expandedSplits={expandedSplits}
      />
    </div>
  );
}
